import { UseMutationOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CustomQueryOptions } from 'api/shared';
import { useBusiness } from 'hooks';
import { IBusinessContext } from 'providers/business-provider/BusinessProvider';
import {
  deleteBusinessLogo,
  getBusinessInfo,
  getBusinessSettings,
  updateBusinessInfo,
  updateBusinessSettings,
  uploadBusinessLogo,
} from './api';
import {
  BusinessInfo,
  BusinessSettings,
  UpdateBusinessRequest,
  UpdateSettingsRequest,
} from './dtos';

export const useBusinessInfo = (
  businessId: number,
  options?: CustomQueryOptions<BusinessInfo, unknown, IBusinessContext>,
) => {
  const query = useQuery({
    queryKey: ['businessInfo', businessId],
    queryFn: () => getBusinessInfo({ businessId }),
    staleTime: Infinity,
    select: (data) => ({
      ...data,
      hasLoaded: true,
      businessId,
    }),
    ...options,
  });

  return query;
};

export const useBusinessSettings = (options?: CustomQueryOptions<BusinessSettings>) => {
  const { businessId } = useBusiness();

  const query = useQuery({
    queryKey: ['businessSettings', businessId],
    queryFn: () => getBusinessSettings({ businessId }),
    staleTime: Infinity,
    ...options,
  });

  return query;
};

export const useUpdateSettings = (
  options?: UseMutationOptions<
    BusinessSettings,
    unknown,
    Omit<UpdateSettingsRequest, 'businessId'>
  >,
) => {
  const { businessId } = useBusiness();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['businessSettings', businessId],
    mutationFn: (data) => updateBusinessSettings({ businessId, ...data }),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: ['businessInfo', businessId],
      });
      queryClient.setQueryData<IBusinessContext | undefined>(
        ['businessInfo', businessId],
        (current) => {
          if (current == null) return;
          return {
            ...current,
            settings: data,
          };
        },
      );
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

export const useUpdateBusinessInfo = (
  options?: UseMutationOptions<BusinessInfo, unknown, Omit<UpdateBusinessRequest, 'businessId'>>,
) => {
  const { businessId } = useBusiness();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['businessInfo', businessId, 'update'],
    mutationFn: (data) => updateBusinessInfo({ businessId, ...data }),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.setQueryData<IBusinessContext | undefined>(
        ['businessInfo', businessId],
        (current) => (current ? { ...current, ...data } : current),
      );
      queryClient.invalidateQueries({ queryKey: ['businessInfo', businessId] });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

export const useUploadBusinessLogo = (options?: UseMutationOptions<unknown, unknown, FormData>) => {
  const { businessId } = useBusiness();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['businessInfo', businessId],
    mutationFn: (data) => uploadBusinessLogo(businessId, data),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: ['businessInfo', businessId],
      });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

export const useDeleteBusinessLogo = (options?: UseMutationOptions<unknown, unknown>) => {
  const { businessId } = useBusiness();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['businessInfo', businessId],
    mutationFn: () => deleteBusinessLogo(businessId),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: ['businessInfo', businessId],
      });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};
